import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import { config, debugLog } from '../utils/config';

interface TreasuryMetrics {
  totalTreasuryETH: string;
  ethFromRevenue: string;
  ethFromSells: string;
  totalMMBuys: string;
  treasuryGrowthPercent: string;
  lastUpdated: string;
}

interface TreasuryTransaction {
  id: string;
  timestamp: string;
  type: 'incoming_revenue' | 'incoming_sell' | 'outgoing_buy';
  ethAmount: string;
  usdValue?: string;
  txHash?: string;
  source: string;
  status: 'confirmed' | 'pending' | 'failed';
  gasUsed?: string;
  description?: string;
  marketMakingDetails?: {
    bwsAmount?: string;
    price?: string;
    exchange?: string;
  };
}

interface ChartDataPoint {
  timestamp: string;
  ethFromRevenue: number;
  ethFromSells: number;
  ethFromBuys: number;
  totalTreasury: number;
}

export const Treasury: React.FC = () => {
  const [metrics, setMetrics] = useState<TreasuryMetrics>({
    totalTreasuryETH: '0',
    ethFromRevenue: '0',
    ethFromSells: '0',
    totalMMBuys: '0',
    treasuryGrowthPercent: '+0%',
    lastUpdated: new Date().toISOString()
  });

  const [incomingTransactions, setIncomingTransactions] = useState<TreasuryTransaction[]>([]);
  const [buyTransactions, setBuyTransactions] = useState<TreasuryTransaction[]>([]);
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTreasuryData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Test LocalStack connection if enabled
      if (config.localstack.enabled) {
        const connectionTest = await api.testLocalStack();
        if (!connectionTest.connected) {
          debugLog('LocalStack connection failed:', connectionTest.error);
          setError(`LocalStack connection failed: ${connectionTest.error}`);
          return;
        }
      }

      // Load treasury data in parallel
      const [treasuryMetrics, treasuryTransactions, treasuryChart] = await Promise.all([
        api.treasury.getMetrics(),
        api.treasury.getTransactions(50),
        api.treasury.getChartData('24h')
      ]);

      setMetrics(treasuryMetrics);
      setChartData(treasuryChart);

      // Separate incoming and outgoing transactions
      const incoming = treasuryTransactions.filter(tx =>
        tx.type === 'incoming_revenue' || tx.type === 'incoming_sell'
      );
      const buys = treasuryTransactions.filter(tx => tx.type === 'outgoing_buy');

      setIncomingTransactions(incoming);
      setBuyTransactions(buys);

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load treasury data';
      debugLog('Treasury data loading error:', errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadTreasuryData();

    // Set up periodic refresh
    const interval = setInterval(loadTreasuryData, config.intervals.medium);

    return () => clearInterval(interval);
  }, []);

  const formatETH = (amount: string) => {
    const num = parseFloat(amount);
    return `${num.toLocaleString(undefined, {
      minimumFractionDigits: 4,
      maximumFractionDigits: 6
    })} ETH`;
  };

  const formatUSD = (amount: string) => {
    const num = parseFloat(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const shortenTxHash = (hash: string) => {
    return `${hash.slice(0, 8)}...${hash.slice(-6)}`;
  };

  if (isLoading) {
    return (
      <div className="container-fluid">
        <div className="placeholder-glow mb-4">
          <div className="placeholder col-4 mb-2" style={{ height: '2rem' }}></div>
          <div className="placeholder col-6" style={{ height: '1rem' }}></div>
        </div>
        <div className="row g-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="col-12 col-md-6 col-lg-3">
              <div className="card placeholder-glow">
                <div className="card-body">
                  <div className="placeholder col-12" style={{ height: '120px' }}></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Page Header */}
      <div className="mb-4">
        <h1 className="h2 mb-2">Treasury Dashboard</h1>
        <p className="text-muted">
          Monitor ETH treasury flows and market making operations
          {config.localstack.enabled && (
            <span className="ms-2 badge bg-primary-subtle text-primary border border-primary">
              <i className="bi bi-hdd-network me-1"></i>
              LocalStack Mode
            </span>
          )}
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="alert alert-danger d-flex align-items-start mb-4" role="alert">
          <i className="bi bi-exclamation-triangle-fill me-3 fs-4"></i>
          <div className="flex-grow-1">
            <h5 className="alert-heading mb-1">Connection Error</h5>
            <p className="mb-2">{error}</p>
            <button
              onClick={loadTreasuryData}
              className="btn btn-sm btn-outline-danger"
            >
              <i className="bi bi-arrow-clockwise me-1"></i>
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Treasury Metrics Overview */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-md-6 col-lg-3">
          <div className="card shadow-sm h-100 border-info">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <p className="text-muted mb-1 small">Total Treasury</p>
                  <h3 className="mb-0">{formatETH(metrics.totalTreasuryETH)}</h3>
                </div>
                <div className="p-2 rounded-circle bg-info-subtle">
                  <i className="bi bi-coin fs-4 text-info"></i>
                </div>
              </div>
              <small className="text-muted">{metrics.treasuryGrowthPercent}</small>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-3">
          <div className="card shadow-sm h-100 border-success">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <p className="text-muted mb-1 small">ETH from Revenue</p>
                  <h3 className="mb-0">{formatETH(metrics.ethFromRevenue)}</h3>
                </div>
                <div className="p-2 rounded-circle bg-success-subtle">
                  <i className="bi bi-arrow-down-left fs-4 text-success"></i>
                </div>
              </div>
              <small className="text-muted">Coinbase conversions</small>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-3">
          <div className="card shadow-sm h-100 border-primary">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <p className="text-muted mb-1 small">ETH from MM Sells</p>
                  <h3 className="mb-0">{formatETH(metrics.ethFromSells)}</h3>
                </div>
                <div className="p-2 rounded-circle bg-primary-subtle">
                  <i className="bi bi-graph-up-arrow fs-4 text-primary"></i>
                </div>
              </div>
              <small className="text-muted">Market making profits</small>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 col-lg-3">
          <div className="card shadow-sm h-100 border-warning">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <p className="text-muted mb-1 small">Total MM Buys</p>
                  <h3 className="mb-0">{formatETH(metrics.totalMMBuys)}</h3>
                </div>
                <div className="p-2 rounded-circle bg-warning-subtle">
                  <i className="bi bi-arrow-up-right fs-4 text-warning"></i>
                </div>
              </div>
              <small className="text-muted">BWS purchases</small>
            </div>
          </div>
        </div>
      </div>

      {/* Treasury Flow Chart */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-transparent">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-bar-chart fs-5 text-primary"></i>
            <h2 className="h5 fw-semibold mb-0">Treasury Flow Analysis</h2>
          </div>
          <p className="small text-muted mb-0">24-hour ETH flow breakdown</p>
        </div>
        <div className="card-body">
          <div style={{ height: '256px' }}>
            <TreasuryFlowChart data={chartData} />
          </div>
        </div>
      </div>

      {/* Transaction Logs */}
      <div className="row g-3 mb-4">
        {/* Incoming ETH Log */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-transparent">
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-arrow-down-left-circle fs-5 text-success"></i>
                <h2 className="h5 fw-semibold mb-0">Incoming ETH</h2>
              </div>
              <p className="small text-muted mb-0">Revenue + MM sell proceeds</p>
            </div>
            <div className="card-body">
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {incomingTransactions.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-file-earmark-text fs-1 d-block mb-2"></i>
                    <p className="mb-0">No incoming transactions yet</p>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {incomingTransactions.map((tx) => (
                      <div key={tx.id} className={`border-start border-4 ps-3 py-2 ${
                        tx.type === 'incoming_revenue' ? 'border-success' : 'border-primary'
                      }`}>
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <p className={`fw-medium mb-1 ${
                              tx.type === 'incoming_revenue' ? 'text-success' : 'text-primary'
                            }`}>
                              {formatETH(tx.ethAmount)}
                            </p>
                            <p className="small text-muted mb-1">
                              {tx.type === 'incoming_revenue' ? 'Revenue' : 'MM Sell'} - {tx.source}
                            </p>
                            <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                              {formatTimestamp(tx.timestamp)}
                            </p>
                            {tx.txHash && (
                              <p className="text-muted font-monospace mb-0" style={{ fontSize: '0.75rem' }}>
                                {shortenTxHash(tx.txHash)}
                              </p>
                            )}
                          </div>
                          <div className="d-flex flex-column align-items-end">
                            <span className={`badge ${
                              tx.status === 'confirmed' ? 'bg-success' :
                              tx.status === 'pending' ? 'bg-warning' : 'bg-danger'
                            }`}>
                              {tx.status}
                            </span>
                            {tx.usdValue && (
                              <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.75rem' }}>
                                {formatUSD(tx.usdValue)}
                              </p>
                            )}
                          </div>
                        </div>
                        {tx.gasUsed && (
                          <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.75rem' }}>Gas: {tx.gasUsed}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Market Making Buys Log */}
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-transparent">
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-arrow-up-right-circle fs-5 text-warning"></i>
                <h2 className="h5 fw-semibold mb-0">MM Buys</h2>
              </div>
              <p className="small text-muted mb-0">BWS token purchases</p>
            </div>
            <div className="card-body">
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {buyTransactions.length === 0 ? (
                  <div className="text-center text-muted py-5">
                    <i className="bi bi-file-earmark-text fs-1 d-block mb-2"></i>
                    <p className="mb-0">No buy transactions yet</p>
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {buyTransactions.map((tx) => (
                      <div key={tx.id} className="border-start border-warning border-4 ps-3 py-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <p className="fw-medium text-warning mb-1">
                              {formatETH(tx.ethAmount)}
                            </p>
                            <p className="small text-muted mb-1">{tx.source}</p>
                            <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>
                              {formatTimestamp(tx.timestamp)}
                            </p>
                            {tx.txHash && (
                              <p className="text-muted font-monospace mb-0" style={{ fontSize: '0.75rem' }}>
                                {shortenTxHash(tx.txHash)}
                              </p>
                            )}
                          </div>
                          <div className="d-flex flex-column align-items-end">
                            <span className={`badge ${
                              tx.status === 'confirmed' ? 'bg-success' :
                              tx.status === 'pending' ? 'bg-warning' : 'bg-danger'
                            }`}>
                              {tx.status}
                            </span>
                            {tx.usdValue && (
                              <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.75rem' }}>
                                {formatUSD(tx.usdValue)}
                              </p>
                            )}
                          </div>
                        </div>
                        {tx.marketMakingDetails && (
                          <div className="text-muted mt-2" style={{ fontSize: '0.75rem' }}>
                            {tx.marketMakingDetails.bwsAmount && (
                              <p className="mb-1">→ {tx.marketMakingDetails.bwsAmount} BWS</p>
                            )}
                            {tx.marketMakingDetails.price && (
                              <p className="mb-1">Price: ${tx.marketMakingDetails.price}</p>
                            )}
                            {tx.marketMakingDetails.exchange && (
                              <p className="mb-0">Exchange: {tx.marketMakingDetails.exchange}</p>
                            )}
                          </div>
                        )}
                        {tx.gasUsed && (
                          <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.75rem' }}>Gas: {tx.gasUsed}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Treasury Summary Stats */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-transparent">
          <h2 className="h5 fw-semibold mb-1">Treasury Summary</h2>
          <p className="small text-muted mb-0">Key metrics and ratios</p>
        </div>
        <div className="card-body">
          <div className="row g-4">
            <div className="col-12 col-md-3">
              <label className="small fw-medium text-muted d-block mb-2">Revenue Efficiency</label>
              <div className="h3 fw-semibold text-success mb-1">
                {metrics.ethFromRevenue && metrics.totalTreasuryETH
                  ? ((parseFloat(metrics.ethFromRevenue) / parseFloat(metrics.totalTreasuryETH)) * 100).toFixed(1)
                  : '0'
                }%
              </div>
              <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>Revenue contribution to treasury</p>
            </div>
            <div className="col-12 col-md-3">
              <label className="small fw-medium text-muted d-block mb-2">MM Profitability</label>
              <div className="h3 fw-semibold text-primary mb-1">
                {metrics.ethFromSells && metrics.totalMMBuys
                  ? ((parseFloat(metrics.ethFromSells) / parseFloat(metrics.totalMMBuys)) * 100).toFixed(1)
                  : '0'
                }%
              </div>
              <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>MM sell proceeds vs buys</p>
            </div>
            <div className="col-12 col-md-3">
              <label className="small fw-medium text-muted d-block mb-2">Net MM Impact</label>
              <div className="h3 fw-semibold text-info mb-1">
                {formatETH(
                  (parseFloat(metrics.ethFromSells || '0') - parseFloat(metrics.totalMMBuys || '0')).toString()
                )}
              </div>
              <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>Sells minus buys (net gain)</p>
            </div>
            <div className="col-12 col-md-3">
              <label className="small fw-medium text-muted d-block mb-2">Last Updated</label>
              <div className="small fw-semibold text-secondary mb-1">
                {formatTimestamp(metrics.lastUpdated)}
              </div>
              <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>Data freshness indicator</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Placeholder chart component - you can implement actual charting library integration
const TreasuryFlowChart: React.FC<{ data: ChartDataPoint[] }> = ({ data }) => {
  return (
    <div className="d-flex align-items-center justify-content-center h-100 bg-secondary-subtle rounded">
      <div className="text-center text-muted">
        <i className="bi bi-bar-chart fs-1 d-block mb-2"></i>
        <p className="fw-medium mb-1">Treasury Flow Chart</p>
        <p className="small mb-1">{data.length} data points available</p>
        <p className="mb-0" style={{ fontSize: '0.75rem' }}>
          Chart visualization would show ETH inflows/outflows over time
        </p>
      </div>
    </div>
  );
};
